import { useState, useRef, useEffect } from "react";
import { X, Send, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { PHONE, TEL } from "@/lib/config";
import { useContent } from "@/lib/ContentContext";

let externalOpen = () => {};
export const openGhost = () => externalOpen();

export const GhostAI = () => {
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const greeting = content.ghost_greeting ||
    "Hi! I'm the Veterans Power Wash virtual assistant. I can help you choose a service, request a free quote, upload photos, or schedule a callback. What can I help you with today?";

  useEffect(() => {
    externalOpen = () => setOpen(true);
    const params = new URLSearchParams(window.location.search);
    if (params.get("ghost") === "1") setOpen(true);
    return () => { externalOpen = () => {}; };
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const r = await api.post("/ghost/chat", {
        conversation_id: convId, message: text, landing_page: window.location.pathname,
      });
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: `Sorry, I'm having trouble right now. Please call us at ${PHONE}.` }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ghost-fab"
        className={`fixed z-40 right-4 bottom-20 lg:bottom-6 h-14 w-14 rounded-full bg-[var(--red)] text-white flex items-center justify-center card-shadow-lg btn-lift ${open ? "hidden" : "ghost-pulse"}`}
        aria-label="Open Ghost AI chat">
        <img src="/brand/ghost.png" alt="Ghost AI" className="h-9 w-9 object-contain" />
      </button>

      {open && (
        <div className="fixed z-50 right-0 bottom-0 sm:right-4 sm:bottom-6 w-full sm:w-[380px] h-[85vh] sm:h-[560px] bg-white sm:rounded-2xl card-shadow-lg flex flex-col overflow-hidden border border-gray-200"
             data-testid="ghost-panel">
          <div className="bg-[var(--navy)] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/brand/ghost.png" alt="" className="h-9 w-9 rounded-lg bg-white/10 p-1" />
              <div className="leading-tight">
                <p className="font-head font-bold text-base">Ghost AI Assistant</p>
                <p className="text-[11px] text-[var(--aqua)]">Veterans Power Wash & Cleaning</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ghost-close" aria-label="Close"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[var(--bg-light)]" data-testid="ghost-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--action)] text-white rounded-br-sm" : "bg-white text-[var(--text-dark)] border border-gray-200 rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-gray-200 px-4 py-2.5 rounded-2xl text-sm text-gray-400">Ghost AI is typing…</div></div>}
            <div ref={endRef} />
          </div>

          <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2 bg-white">
            <a href={TEL} className="text-xs text-[var(--red)] font-semibold flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Call</a>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…" data-testid="ghost-input"
              className="flex-1 text-sm px-3 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--action)]" />
            <button onClick={send} disabled={loading} data-testid="ghost-send"
              className="h-9 w-9 rounded-full bg-[var(--red)] text-white flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
